import { useDeferredValue, useMemo, useState } from 'react'
import { z } from 'zod'
import { type UseFormReturn, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { PageHeading } from '@/components/page-heading'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useClubs,
  useCreateClub,
  useDeleteClub,
  useOrganizations,
  useUpdateClub,
} from '@/api/hooks'
import type { TClub } from '@schemas'

const CLUBS_PAGE_LIMIT = 100

const NO_ORGANIZATION_VALUE = 'none'

const clubFormSchema = z.object({
  name: z.string().min(1, 'Club name is required'),
  organizationId: z.union([z.guid(), z.literal(NO_ORGANIZATION_VALUE)]),
})

type ClubFormValues = z.infer<typeof clubFormSchema>
type OrganizationFilterValue = 'all' | 'unassigned' | string

function formatClubName(name: string) {
  return name.trim()
}

function normalizeOrganizationValue(value: string) {
  return value === NO_ORGANIZATION_VALUE ? null : value
}

export default function ClubsPage() {
  const organizationsQuery = useOrganizations()
  const organizations = useMemo(() => organizationsQuery.data ?? [], [organizationsQuery.data])

  const organizationLookup = useMemo(() => {
    return organizations.reduce<Record<string, string>>((map, org) => {
      if (org.id) {
        map[org.id] = org.name
      }
      return map
    }, {})
  }, [organizations])

  const [organizationFilter, setOrganizationFilter] = useState<OrganizationFilterValue>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const deferredSearch = useDeferredValue(searchTerm)

  const clubsQueryArgs = useMemo(() => {
    const trimmed = deferredSearch.trim()
    if (organizationFilter === 'all') {
      return {
        q: trimmed || undefined,
        limit: CLUBS_PAGE_LIMIT,
      }
    }
    if (organizationFilter === 'unassigned') {
      return {
        q: trimmed || undefined,
        organizationId: null,
        limit: CLUBS_PAGE_LIMIT,
        includeGlobal: false,
      }
    }
    return {
      q: trimmed || undefined,
      organizationId: organizationFilter,
      limit: CLUBS_PAGE_LIMIT,
      includeGlobal: true,
    }
  }, [deferredSearch, organizationFilter])

  const clubsQuery = useClubs(clubsQueryArgs, { enabled: organizations.length > 0 })

  const clubs = useMemo(() => {
    const data = clubsQuery.data ?? []
    const collator = new Intl.Collator(undefined, { usage: 'sort', sensitivity: 'base' })
    if (organizationFilter === 'unassigned') {
      return data
        .filter(club => !club.organizationId)
        .sort((a, b) => collator.compare(a.name, b.name))
    }
    return [...data].sort((a, b) => collator.compare(a.name, b.name))
  }, [clubsQuery.data, organizationFilter])

  const createClub = useCreateClub()
  const updateClub = useUpdateClub()
  const deleteClub = useDeleteClub()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editingClub, setEditingClub] = useState<TClub | null>(null)
  const [clubToDelete, setClubToDelete] = useState<TClub | null>(null)

  const createForm = useForm<ClubFormValues>({
    resolver: zodResolver(clubFormSchema),
    defaultValues: {
      name: '',
      organizationId: NO_ORGANIZATION_VALUE,
    },
  })

  const editForm = useForm<ClubFormValues>({
    resolver: zodResolver(clubFormSchema),
    defaultValues: {
      name: '',
      organizationId: NO_ORGANIZATION_VALUE,
    },
  })

  const isLoading = clubsQuery.isLoading || organizationsQuery.isLoading
  const hasError = clubsQuery.isError || organizationsQuery.isError

  const handleSubmitCreate = async (values: ClubFormValues) => {
    try {
      const name = formatClubName(values.name)
      if (!name) {
        createForm.setError('name', { message: 'Club name is required' })
        return
      }

      await createClub.mutateAsync({
        name,
        organizationId: normalizeOrganizationValue(values.organizationId) ?? undefined,
      })
      toast.success(`Created club “${name}”`)
      setCreateOpen(false)
      createForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to create club. Try again shortly.'
      toast.error(message)
    }
  }

  const handleSubmitEdit = async (values: ClubFormValues) => {
    if (!editingClub?.id) return
    try {
      const name = formatClubName(values.name)
      if (!name) {
        editForm.setError('name', { message: 'Club name is required' })
        return
      }

      await updateClub.mutateAsync({
        clubId: editingClub.id,
        payload: {
          name,
          organizationId: normalizeOrganizationValue(values.organizationId),
        },
      })
      toast.success(`Updated club “${name}”`)
      setEditOpen(false)
      setEditingClub(null)
      editForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to update club. Try again shortly.'
      toast.error(message)
    }
  }

  const handleConfirmDelete = async () => {
    if (!clubToDelete?.id) return
    try {
      await deleteClub.mutateAsync({ clubId: clubToDelete.id })
      toast.success(`Deleted club “${clubToDelete.name}”`)
      setDeleteOpen(false)
      setClubToDelete(null)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to delete club. Try again shortly.'
      toast.error(message)
    }
  }

  const handleOpenEdit = (club: TClub) => {
    setEditingClub(club)
    editForm.reset({
      name: club.name,
      organizationId: club.organizationId ?? NO_ORGANIZATION_VALUE,
    })
    setEditOpen(true)
  }

  const handleOpenDelete = (club: TClub) => {
    setClubToDelete(club)
    setDeleteOpen(true)
  }

  const organizationOptions = useMemo(
    () =>
      organizations
        .filter(org => Boolean(org.id))
        .map(org => ({
          id: org.id as string,
          name: org.name,
        })),
    [organizations]
  )

  const renderOrganization = (club: TClub) =>
    club.organizationId ? (
      (organizationLookup[club.organizationId] ?? (
        <span className="text-muted-foreground">Unknown organization</span>
      ))
    ) : (
      <Badge variant="secondary">Global</Badge>
    )

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <PageHeading>Clubs</PageHeading>
        <p className="text-muted-foreground max-w-2xl">
          Manage the shared directory of clubs across all shows. Use the filters to review
          organization-specific rosters, add new records, or update existing ones before intake.
        </p>
      </section>

      <Card>
        <CardHeader className="flex flex-col items-stretch gap-3">
          <div>
            <CardTitle className="text-xl">Club Directory</CardTitle>
            <CardDescription>
              Search by name or narrow the list to a single organization to keep intake aligned.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={organizationFilter}
              onValueChange={value => setOrganizationFilter(value as OrganizationFilterValue)}
            >
              <SelectTrigger
                className="w-auto max-w-full min-w-[min(14rem,100%)] grow"
                aria-label="Filter by organization"
                title={organizationLookup[organizationFilter]}
              >
                <SelectValue placeholder="Filter by organization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                <SelectItem value="unassigned">Global (no organization)</SelectItem>
                {organizationOptions.map(option => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search clubs…"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              className="grow basis-64"
              aria-label="Search clubs"
            />
            <Sheet
              open={createOpen}
              onOpenChange={open => {
                setCreateOpen(open)
                if (!open) {
                  createForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
                }
              }}
            >
              <SheetContent side="right" className="sm:max-w-md">
                <SheetHeader>
                  <SheetTitle>Create club</SheetTitle>
                  <SheetDescription>
                    Add a new club to the global directory. Link it to an organization to scope it
                    to a single show.
                  </SheetDescription>
                </SheetHeader>
                <ClubForm
                  form={createForm}
                  organizations={organizationOptions}
                  onSubmit={handleSubmitCreate}
                  onCancel={() => {
                    setCreateOpen(false)
                    createForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
                  }}
                  submitLabel="Create club"
                  isSubmitting={createClub.isPending}
                />
              </SheetContent>
              <Button className="w-full @sm/card-header:w-auto" onClick={() => setCreateOpen(true)}>
                New club
              </Button>
            </Sheet>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : hasError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Unable to load clubs. Refresh the page or try again later.
            </div>
          ) : (
            <div className="@container/club-table space-y-3">
              {clubsQuery.data && clubsQuery.data.length >= CLUBS_PAGE_LIMIT ? (
                <p className="text-muted-foreground text-sm">
                  Showing the first {CLUBS_PAGE_LIMIT} clubs. Search or choose an organization to
                  narrow the list.
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden @xl/club-table:table-cell">Organization</TableHead>
                    <TableHead className="w-px text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clubs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-12 text-center whitespace-normal text-muted-foreground"
                      >
                        No clubs match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clubs.map(club => (
                      <TableRow key={club.id}>
                        <TableCell className="whitespace-normal wrap-anywhere">
                          <span className="font-medium">{club.name}</span>
                          {/* Narrow cards drop the Organization column and show it here. */}
                          <div className="mt-1 text-xs text-muted-foreground @xl/club-table:hidden">
                            {renderOrganization(club)}
                          </div>
                        </TableCell>
                        <TableCell className="hidden whitespace-normal wrap-anywhere @xl/club-table:table-cell">
                          {renderOrganization(club)}
                        </TableCell>
                        <TableCell className="w-px">
                          <div className="flex flex-col items-end gap-2 @sm/club-table:flex-row @sm/club-table:justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenEdit(club)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleOpenDelete(club)}
                              disabled={deleteClub.isPending && clubToDelete?.id === club.id}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={editOpen}
        onOpenChange={open => {
          setEditOpen(open)
          if (!open) {
            setEditingClub(null)
            editForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
          }
        }}
      >
        <SheetContent side="right" className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit club</SheetTitle>
            <SheetDescription>
              Update the club name or move it to another organization. Changes apply immediately.
            </SheetDescription>
          </SheetHeader>
          <ClubForm
            form={editForm}
            organizations={organizationOptions}
            onSubmit={handleSubmitEdit}
            onCancel={() => {
              setEditOpen(false)
              setEditingClub(null)
              editForm.reset({ name: '', organizationId: NO_ORGANIZATION_VALUE })
            }}
            submitLabel="Save changes"
            isSubmitting={updateClub.isPending}
          />
        </SheetContent>
      </Sheet>

      <Sheet
        open={deleteOpen}
        onOpenChange={open => {
          setDeleteOpen(open)
          if (!open) {
            setClubToDelete(null)
          }
        }}
      >
        <SheetContent side="bottom" className="sm:max-w-xl sm:mx-auto sm:rounded-t-lg">
          <SheetHeader>
            <SheetTitle>Delete club</SheetTitle>
            <SheetDescription>
              This action permanently removes the club from the directory. Entries that reference it
              must be reassigned before deletion succeeds.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4">
            <p className="text-sm">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{clubToDelete?.name ?? 'this club'}</span>?
            </p>
          </div>
          <SheetFooter className="flex-col gap-2 px-4 pb-6 sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteOpen(false)
                setClubToDelete(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteClub.isPending}
            >
              Delete
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}

type ClubFormProps = {
  form: UseFormReturn<ClubFormValues>
  organizations: Array<{ id: string; name: string }>
  onSubmit: (values: ClubFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
  isSubmitting: boolean
}

export function ClubForm({
  form,
  organizations,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
}: ClubFormProps) {
  return (
    <Form {...form}>
      <form
        onSubmit={event => {
          void form.handleSubmit(values => onSubmit(values))(event)
        }}
        className="flex flex-1 flex-col gap-6 px-4 pb-6"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Club name</FormLabel>
              <FormControl>
                <Input placeholder="Example Beekeepers Association" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="organizationId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization scope</FormLabel>
              <Select value={field.value} onValueChange={value => field.onChange(value)}>
                <FormControl>
                  <SelectTrigger aria-label="Organization scope">
                    <SelectValue placeholder="Available for all shows" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_ORGANIZATION_VALUE}>Global (no organization)</SelectItem>
                  {organizations.map(option => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  )
}
